function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { memo } from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import { useTheme } from 'emotion-theming';
import * as ThemeColors from '../../../core/Colors';
/**
 * This component represent a custom check box.
 * @param {string} className ClassName used to apply predetermine styles.
 * @param {boolean} checked Boolean to know if checked or not.
 * @param {string} checkColor Color for checked.
 * @param {string} uncheckColor Color for unchecked.
 * @param {*} props All other supported props.
 */

export const Checkbox = /*#__PURE__*/memo(({
  className,
  checked,
  checkColor,
  uncheckColor,
  ...props
}) => jsx(CheckboxContainer, {
  className: className
}, jsx(HiddenCheckbox, _extends({
  checked: checked
}, props)), jsx(StyledCheckbox, {
  checked: checked,
  checkColor: checkColor,
  uncheckColor: uncheckColor
})));
const Icon = styled.svg`
  fill: none;
  stroke: white;
  stroke-width: 2px;
`;
/**
 * This component represent a check box with icon.
 * @param {string} className ClassName used to apply predetermine styles.
 * @param {boolean} checked Boolean to know if checked or not.
 * @param {string} checkColor Color for checked.
 * @param {string} uncheckColor Color for unchecked.
 * @param {*} children Text in the check box.
 * @param {*} props All other supported props.
 */

export const CheckboxWithIcon = /*#__PURE__*/memo(({
  className,
  checked,
  checkColor,
  uncheckColor,
  children,
  ...props
}) => jsx(CheckboxContainer, {
  className: className
}, jsx(HiddenCheckbox, _extends({
  checked: checked
}, props)), jsx(StyledCheckbox, {
  checked: checked,
  checkColor: checkColor,
  uncheckColor: uncheckColor
}, jsx(Icon, {
  viewBox: "0 0 24 24"
}, jsx("polyline", {
  points: "20 6 9 17 4 12"
})))));
const HiddenCheckbox = styled.input.attrs({
  type: 'checkbox'
})`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
const StyledCheckbox = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  background: ${props => props.checked ? props.checkColor : props.uncheckColor};
  border-radius: 3px;
  transition: all 150ms;
  border: 1px solid #dadada;
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 3px #dadada;
  }
  ${Icon} {
    visibility: ${props => props.checked ? 'visible' : 'hidden'};
  }
`;
const CheckboxContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  margin-right: 10px;
`;
/**
 * This component represent a simple check box.
 * @param {*} children Text in the check box.
 * @param {string} text Text in the check box.
 * @param {string} color Color for check box.
 * @param {JSON} containerStyle Custom styles apply to the div that wraps the checkbox.
 * @param {string} className ClassName used to apply predetermine styles to the div that wraps the checkbox.
 * @param {boolean} disabled Boolean to know if checked or not.
 * @param {*} rest All other supported props.
 */

export const SimpleCheckBox = /*#__PURE__*/memo(({
  children,
  text,
  color = 'fireEngineRed',
  containerStyle,
  className,
  disabled = false,
  ...rest
}) => {
  return jsx("div", {
    css: checkBox(color),
    style: {
      display: 'flex',
      flexDirection: 'row',
      ...containerStyle
    }
  }, jsx("div", {
    className: classnames('checkbox-container', className)
  }, jsx("input", _extends({
    type: "checkbox"
  }, rest, {
    disabled: disabled
  })), jsx("span", {
    className: "checkmark"
  })), jsx("div", null, jsx("label", null, children || text)));
});
/**
 * This component represent a check box.
 * @param {string} color Color for check box.
 */

const checkBox = color => {
  const {
    theme
  } = useTheme();
  return css`
    label {
      color: #333333;
      font-size: 12px;
    }

    .checkbox-container {
      position: relative;
      cursor: pointer;
      width: 24px;
      height: 24px;
      margin-right: 11px;
    }

    .checkbox-container input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 100%;
      width: 100%;
      margin: 0;
      z-index: 10;
    }

    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 18px;
      width: 18px;
      border: 2px solid ${ThemeColors[theme][color]};
      border-radius: 4px;
    }

    .checkbox-container input:checked ~ .checkmark {
      background-color: ${ThemeColors[theme][color]};
      border: 1px solid ${ThemeColors[theme][color]};
      height: 20px;
      width: 20px;
    }

    .checkmark:after {
      content: '';
      position: absolute;
      display: none;
    }

    .checkbox-container input:checked ~ .checkmark:after {
      display: block;
    }

    .checkbox-container .checkmark:after {
      left: 6px;
      top: 3px;
      width: 6px;
      height: 10px;
      border: 1px solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    input[readonly],
    input:disabled {
      cursor: not-allowed;
    }
  `;
};